import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faMobileAlt,
  faCreditCard,
  faSpinner,
  faWallet,
  faCheckCircle,
  faRedo,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { RoleGuard } from "../App";
import { StandardURL } from "../App";

const WithdrawalsPage = () => {
  const [balance, setBalance] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [showMobileMoneyOptions, setShowMobileMoneyOptions] = useState(false);
  const [showCardPaymentOptions, setShowCardPaymentOptions] = useState(false);
  const [bankAccount, setBankAccount] = useState("");
  const [senderNames, setSenderNames] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOTPOpen, setIsOTPOpen] = useState(false);
  const [isOTPCardOpen, setIsOTPCardOpen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const [maxWithdrawalAmount, setMaxWithdrawalAmount] = useState(0);

  const token = localStorage.getItem("token");
  const propertyId = localStorage.getItem("selectedPropertyId");
  const userDataString = localStorage.getItem("user");
  const userData = JSON.parse(userDataString);
  const ownerId = userData.unique;

  const otpInputsRef = useRef([]);
  const otpCardInputsRef = useRef([]);

  useEffect(() => {
    if (token && propertyId) {
      // Fetch regular balance
      axios
        .get(
          `${StandardURL}/iNyumba/balance/owner/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setBalance(
            response.data.response.length > 0
              ? response.data.response[0].balance
              : 0
          );
        })
        .catch((error) => {
          console.error("Error fetching balance:", error);
        });

      // Fetch maximum withdrawal amount
      axios
        .get(
          `${StandardURL}/iNyumba/b2cbalancepropertyindividual/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          // Adjust this based on actual API response structure
          setMaxWithdrawalAmount(response.data.response || 0);
        })
        .catch((error) => {
          console.error("Error fetching max withdrawal amount:", error);
        });
    }

    // Fetch banks
    axios
      .get(`${StandardURL}/iNyumba/admin/bank/fetch`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setBanks(response.data.response);
      })
      .catch((error) => {
        console.error("Error fetching banks:", error);
      });
  }, [token, propertyId]);

  const handleBankSelect = (bankCode) => {
    setBankCode(bankCode);
    setSelectedBank(bankCode);
  };

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();

    // Validate maximum withdrawal amount
    if (Number(withdrawalAmount) > maxWithdrawalAmount) {
      toast.error(`Withdrawal amount cannot exceed ${maxWithdrawalAmount}`);
      return;
    }

    if (!token || !propertyId || !withdrawalAmount) {
      console.error(
        "Token, property ID, withdrawal amount, and channel are required."
      );
      return;
    }

    setIsLoading(true);

    try {
      let response;

      if (selectedMode === "mobilemoney") {
        const requestData = {
          amount: withdrawalAmount,
          phone_number: phoneNumber,
          channel: selectedChannel,
        };

        const jsonData = JSON.stringify(requestData);
        response = await axios.post(
          `${StandardURL}/iNyumba/landlordsettlementinitiated/${propertyId}/${ownerId}`,
          jsonData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.responseCode === 200) {
          if (
            response.data.response ===
            "Withdrawal has been blocked kindly contact admin"
          ) {
            toast.info("Withdrawal has been blocked. Kindly contact admin.");
          } else if (response.data.response === "Check your email for OTP") {
            toast.info("Check your email for OTP");
            setIsOTPOpen(true);
          } else if (
            response.data.response.includes(
              "You have insufficient funds in your account. Transaction cost is"
            )
          ) {
            toast.error(response.data.response);
          } else if (response.data.responseCode === 201) {
            toast.error(response.data.response);
          } else {
            handleResponseErrors(response.data.response);
          }
        }
      } else if (selectedMode === "cards") {
        if (!selectedBank) {
          toast.error("Please select a bank");
          return;
        }

        const requestData = {
          amount: withdrawalAmount,
          bankaccount: bankAccount,
          bankcode: bankCode,
          sendernames: senderNames,
        };

        const jsonData = JSON.stringify(requestData);

        response = await axios.post(
          `${StandardURL}/iNyumba/landlordsettlementcardsinitiated/${propertyId}/${ownerId}`,
          jsonData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.responseCode === 200) {
          if (
            response.data.response ===
            "Withdrawal has been blocked kindly contact admin"
          ) {
            toast.info("Withdrawal has been blocked. Kindly contact admin.");
          } else if (response.data.response === "Check your email for OTP") {
            toast.info("Check your email for OTP");
            setIsOTPOpen(false);
            setIsOTPCardOpen(true);
          } else if (
            response.data.response ===
            "You have insufficient funds in your account. Transaction cost is 150"
          ) {
            toast.error(
              "You have insufficient funds in your account. Transaction cost is 150"
            );
          } else if (response.data.responseCode === 201) {
            toast.error(response.data.response);
          }
        } else {
          handleResponseErrors(response.data.response);
        }
      }
    } catch (error) {
      toast.error(
        error.message || "Error submitting withdrawal. Please try again."
      );
      console.error("Error submitting withdrawal:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResponseErrors = (response) => {
    switch (response) {
      case "Hash mismatch error. Please check your data and try again.":
        toast.error("Hash mismatch error. Please verify your data.");
        break;
      case "Transaction reference exists. Please generate a unique reference.":
        toast.error("Reference exists. Please use a unique reference.");
        break;
      case "One or more parameters are missing.":
        toast.error(
          "Some required parameters are missing. Please check and try again."
        );
        break;
      case "Request from unregistered IP address. Please ensure your IP is registered.":
        toast.error(
          "Unregistered IP address. Please verify your network settings."
        );
        break;
      case "Insufficient funds in your iPay bulkpay account.":
        toast.error("Insufficient funds in your iPay bulkpay account.");
        break;
      default:
        toast.error("Bad request. Please check your input and try again.");
    }
  };

  const handleVerifyOTP = async () => {
    const otpCode = otp.join("");

    if (!otpCode || otpCode.length !== 5) {
      toast.error("Please enter a valid 5-digit OTP.");
      return;
    }

    const requestData = {
      amount: withdrawalAmount,
      phone_number: phoneNumber,
      channel: selectedChannel,
      otp: otpCode,
    };

    setIsLoading(true);

    const jsonData = JSON.stringify(requestData);

    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/landlordsettlementcompletesettle/${propertyId}/${ownerId}`,
        jsonData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.responseCode === 200) {
        if (
          response.data.response ===
          "Withdrawal has been blocked kindly contact admin"
        ) {
          toast.info("Withdrawal has been blocked. Kindly contact admin.");
          setIsLoading(false);
        } else if (
          response.data.response ===
          "Your account has reached the OTP limit of 5"
        ) {
          toast.error("Your account has reached the OTP limit of 5.");
          setIsLoading(false);
        } else if (response.data.response === "Invalid OTP") {
          setIsLoading(false);
          toast.error(response.data.response);
        } else if (response.data.response === "B2C blocked request") {
          setIsLoading(false);
          toast.error(response.data.response);
          closeModal();
        } else if (response.data.response === "B2C processing request") {
          setIsLoading(false);
          toast.info(response.data.response);
          closeModal();
        } else {
          setIsLoading(false);
          toast.success(response.data.response);
        }
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Error verifying OTP. Please try again.");
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      const response = await axios.patch(
        `${StandardURL}/iNyumba/landlordsettlementotpresend/${propertyId}/${ownerId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.responseCode === 200) {
        toast.success("OTP resent successfully.");
        setIsLoading(false);
      } else {
        toast.error(response.data.response || "Error resending OTP.");
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Error resending OTP. Please try again.");
    }
  };

  const handleVerifyCardOTP = async () => {
    const otpCode = otp.join("");

    if (!otpCode || otpCode.length !== 5) {
      toast.error("Please enter a valid 5-digit OTP.");
      return;
    }

    setIsLoading(true);

    const requestData = {
      amount: withdrawalAmount,
      bankaccount: bankAccount,
      bankcode: bankCode,
      sendernames: senderNames,
      otp: otpCode,
    };

    const jsonData = JSON.stringify(requestData);

    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/landlordsettlementcardscompletesettle/${propertyId}/${ownerId}`,
        jsonData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.responseCode === 200) {
        if (
          response.data.response ===
          "Withdrawal has been blocked kindly contact admin"
        ) {
          toast.info("Withdrawal has been blocked. Kindly contact admin.");
          setIsLoading(false);
        } else if (
          response.data.response ===
          "Your account has reached the OTP limit of 5"
        ) {
          toast.error("Your account has reached the OTP limit of 5.");
          setIsLoading(false);
        } else if (response.data.response === "Invalid OTP") {
          toast.error(response.data.response);
          setIsLoading(false);
        } else if (response.data.response === "B2C blocked request") {
          toast.error(response.data.response);
          setIsLoading(false);
          closeModal();
        } else if (response.data.response === "B2C processing request") {
          setIsLoading(false);
          toast.info(response.data.response);
          closeModal();
        } else {
          setIsLoading(false);
          toast.info(response.data.response);
        }
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Error verifying OTP. Please try again.");
    }
  };

  const handleResendCardOTP = async () => {
    setIsLoading(true);
    try {
      const response = await axios.patch(
        `${StandardURL}/iNyumba/landlordsettlementcardsotpresend/${propertyId}/${ownerId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.responseCode === 200) {
        setIsLoading(false);
        toast.success("OTP resent successfully.");
      } else {
        setIsLoading(false);
        toast.error(response.data.response || "Error resending OTP.");
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Error resending OTP. Please try again.");
    }
  };

  const closeModal = () => {
    setIsOTPOpen(false);
    setIsOTPCardOpen(false);
    setOtp(["", "", "", "", ""]);
    setWithdrawalAmount("");
    setPhoneNumber("");
    setSelectedChannel(null);
    setShowMobileMoneyOptions(false);
    setShowCardPaymentOptions(false);
    setSelectedMode(null);
    setBankCode("");
    setSenderNames("");
    setSelectedBank("");
    setBankAccount("");
  };

  const toggleShowBalance = () => {
    setShowBalance(!showBalance);
  };

  const handleModeSelection = (mode) => {
    setSelectedMode(mode);

    if (mode === "mobilemoney") {
      setShowMobileMoneyOptions(true);
      setShowCardPaymentOptions(false);
    } else if (mode === "cards") {
      setShowCardPaymentOptions(true);
      setShowMobileMoneyOptions(false);
    } else {
      setShowMobileMoneyOptions(false);
      setShowCardPaymentOptions(false);
    }
  };

  const handleMobileMoneyClick = (channel) => {
    setSelectedChannel(channel);
  };

  const handleOtpChange = (index, value) => {
    if (/^\d*$/.test(value)) {
      setOtp((prevOtp) => {
        const newOtp = [...prevOtp];
        newOtp[index] = value;

        // Auto-focus logic:
        // If a digit is entered and it's not the last input, focus the next.
        if (value !== "" && index < newOtp.length - 1) {
          // Determine which set of refs to use based on which modal is open
          const currentRefArray = isOTPOpen ? otpInputsRef.current : otpCardInputsRef.current;
          if (currentRefArray[index + 1]) {
            currentRefArray[index + 1].focus();
          }
        }
        return newOtp;
      });
    }
  };
 return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-3xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200">
            {/* --- Header Section --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight flex items-center">
                Withdraw Funds <FontAwesomeIcon icon={faWallet} className="ml-4 text-blue-600" />
              </h1>
            </div>

            <p className="text-gray-600 mb-8 max-w-2xl mx-auto text-center">
              Easily withdraw your available funds through mobile money or bank transfer.
            </p>

            {/* --- Balance & Withdrawal Info Card --- */}
            <div className="border border-blue-200 rounded-xl p-6 bg-blue-50 mb-8 shadow-inner">
              <h2 className="text-xl font-semibold text-blue-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faWallet} className="mr-3" /> Your Account Overview
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-blue-100 last:border-b-0">
                  <span className="text-gray-700 font-medium">Current Balance:</span>
                  <div className="flex items-center">
                    <span className="font-bold text-lg text-blue-700">
                      {showBalance ? `Ksh ${parseFloat(balance).toLocaleString()}` : "******"}
                    </span>
                    <button
                      onClick={toggleShowBalance}
                      className="ml-4 p-2 rounded-full hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors"
                      aria-label={showBalance ? "Hide balance" : "Show balance"}
                    >
                      <FontAwesomeIcon
                        icon={showBalance ? faEyeSlash : faEye}
                        className="text-blue-500 text-lg"
                      />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-700 font-medium">Available for Withdrawal:</span>
                  <span className="font-bold text-lg text-green-600">
                    {showBalance ? `Ksh ${parseFloat(maxWithdrawalAmount).toLocaleString()}` : "******"}
                  </span>
                </div>
              </div>
            </div>

            {/* --- Withdrawal Form --- */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">Select Withdrawal Mode</h2>

              {/* Mode Selection */}
              <div className="mb-6">
                <label className="block mb-3 font-semibold text-gray-700 text-lg">Choose Mode:</label>
                <div className="flex flex-wrap gap-4">
                  <button
                    className={`flex items-center px-5 py-3 rounded-lg shadow-sm font-semibold transition-all duration-200
                      ${selectedMode === "mobilemoney"
                        ? "bg-blue-600 text-white shadow-blue-300 transform scale-105"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    onClick={() => handleModeSelection("mobilemoney")}
                  >
                    <FontAwesomeIcon icon={faMobileAlt} className="mr-3 text-xl" />
                    Mobile Money
                  </button>
                  <button
                    className={`flex items-center px-5 py-3 rounded-lg shadow-sm font-semibold transition-all duration-200
                      ${selectedMode === "cards"
                        ? "bg-blue-600 text-white shadow-blue-300 transform scale-105"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    onClick={() => handleModeSelection("cards")}
                  >
                    <FontAwesomeIcon icon={faCreditCard} className="mr-3 text-xl" />
                    Bank Account
                  </button>
                </div>
              </div>

              {/* Mobile Money Options */}
              {showMobileMoneyOptions && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <label className="block mb-3 font-semibold text-gray-700 text-lg">Select Channel:</label>
                  <div className="flex flex-wrap gap-4 mb-5">
                    <button
                      className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors
                        ${selectedChannel === "mpesa"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2`}
                      onClick={() => handleMobileMoneyClick("mpesa")}
                    >
                      M-Pesa
                    </button>
                    <button
                      className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors
                        ${selectedChannel === "airtelmoney"
                          ? "bg-red-500 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2`}
                      onClick={() => handleMobileMoneyClick("airtelmoney")}
                    >
                      Airtel Money
                    </button>
                  </div>

                  <label htmlFor="mm-amount" className="block mb-2 font-semibold text-gray-700">Amount (Ksh)</label>
                  <input
                    id="mm-amount"
                    type="number"
                    value={withdrawalAmount}
                    placeholder={`Enter amount (max: ${maxWithdrawalAmount})`}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || (Number(value) <= maxWithdrawalAmount && Number(value) >= 0)) {
                        setWithdrawalAmount(value);
                      }
                    }}
                    className="w-full border border-gray-300 rounded-md p-3 mb-4 text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    max={maxWithdrawalAmount}
                  />

                  <label htmlFor="phone-number" className="block mb-2 font-semibold text-gray-700">Phone Number</label>
                  <input
                    id="phone-number"
                    type="text"
                    value={phoneNumber}
                    placeholder="e.g., 2547XXXXXXXX or 2541XXXXXXXX"
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-3 mb-4 text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  />
                </div>
              )}

              {/* Bank Account Options */}
              {showCardPaymentOptions && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <label htmlFor="bank-amount" className="block mb-2 font-semibold text-gray-700">Amount (Ksh)</label>
                  <input
                    id="bank-amount"
                    type="number"
                    value={withdrawalAmount}
                    placeholder={`Enter amount (max: ${maxWithdrawalAmount})`}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || (Number(value) <= maxWithdrawalAmount && Number(value) >= 0)) {
                        setWithdrawalAmount(value);
                      }
                    }}
                    className="w-full border border-gray-300 rounded-md p-3 mb-4 text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    max={maxWithdrawalAmount}
                  />

                  <label htmlFor="sender-names" className="block mb-2 font-semibold text-gray-700">Account Name (e.g., John Doe)</label>
                  <input
                    id="sender-names"
                    type="text"
                    value={senderNames}
                    onChange={(e) => setSenderNames(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-3 mb-4 text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="Enter account holder's full name"
                  />

                  <label htmlFor="bank-account" className="block mb-2 font-semibold text-gray-700">Bank Account Number</label>
                  <input
                    id="bank-account"
                    type="text"
                    value={bankAccount}
                    onChange={(e) => setBankAccount(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-3 mb-4 text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="Enter bank account number"
                  />

                  <label htmlFor="select-bank" className="block mb-2 font-semibold text-gray-700">Select Bank</label>
                  <select
                    id="select-bank"
                    value={selectedBank}
                    onChange={(e) => handleBankSelect(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-3 text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  >
                    <option value="">-- Select Your Bank --</option>
                    {banks.map((bank) => (
                      <option key={bank.bankcode} value={bank.bankcode}>
                        {bank.bankname}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Submit Withdrawal Button */}
              <div className="mt-8 flex justify-end">
                <button
                  onClick={handleWithdrawalSubmit}
                  disabled={isLoading}
                  className={`inline-flex items-center justify-center px-8 py-3 rounded-lg text-lg font-semibold shadow-md transition-all duration-300 ease-in-out
                    ${isLoading
                      ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:-translate-y-0.5"
                    }`}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-3 text-xl" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-3 text-xl" />
                      Submit Withdrawal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* --- OTP Modals --- */}
          {isOTPOpen && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 backdrop-blur-sm">
              <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-sm animate-fade-in-up">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Enter OTP Code</h2>
                <p className="text-gray-600 text-center mb-6">
                  A verification code has been sent to your mobile. Please enter it below.
                </p>
                <div className="flex justify-center space-x-3 mb-6">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      className="border border-gray-300 text-center rounded-lg p-3 w-12 text-2xl font-mono focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-colors"
                      // 🔑 Corrected ref assignment
                      ref={(el) => (otpInputsRef.current[index] = el)}
                      onKeyUp={(e) => {
                        // Use the ref array directly
                        if (e.key === 'Backspace' && !digit && index > 0) {
                          otpInputsRef.current[index - 1]?.focus(); // Added optional chaining
                        } else if (e.target.value && index < otp.length - 1) {
                          otpInputsRef.current[index + 1]?.focus(); // Added optional chaining
                        }
                      }}
                    />
                  ))}
                </div>
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={handleVerifyOTP}
                    disabled={isLoading}
                    className={`inline-flex items-center justify-center w-full px-6 py-3 rounded-lg text-lg font-semibold shadow-md transition-all duration-300 ease-in-out
                      ${isLoading
                        ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      }`}
                  >
                    {isLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-3" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-3" />
                        Verify OTP
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleResendOTP}
                    disabled={isLoading}
                    className={`inline-flex items-center justify-center w-full px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-300 ease-in-out
                      ${isLoading
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : "bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400"
                      }`}
                  >
                    {isLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-3" />
                        Resending...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faRedo} className="mr-3" />
                        Resend OTP
                      </>
                    )}
                  </button>
                  <button
                    onClick={closeModal}
                    className="inline-flex items-center justify-center w-full px-6 py-3 rounded-lg text-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors duration-200"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} className="mr-3" />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {isOTPCardOpen && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 backdrop-blur-sm">
              <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-sm animate-fade-in-up">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Enter Bank OTP Code</h2>
                <p className="text-gray-600 text-center mb-6">
                  A verification code has been sent to your registered number.
                </p>
                <div className="flex justify-center space-x-3 mb-6">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      className="border border-gray-300 text-center rounded-lg p-3 w-12 text-2xl font-mono focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-colors"
                      // 🔑 Corrected ref assignment for card OTP
                      ref={(el) => (otpCardInputsRef.current[index] = el)}
                      onKeyUp={(e) => {
                        // Use the ref array directly
                        if (e.key === 'Backspace' && !digit && index > 0) {
                          otpCardInputsRef.current[index - 1]?.focus(); // Added optional chaining
                        } else if (e.target.value && index < otp.length - 1) {
                          otpCardInputsRef.current[index + 1]?.focus(); // Added optional chaining
                        }
                      }}
                    />
                  ))}
                </div>
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={handleVerifyCardOTP}
                    disabled={isLoading}
                    className={`inline-flex items-center justify-center w-full px-6 py-3 rounded-lg text-lg font-semibold shadow-md transition-all duration-300 ease-in-out
                      ${isLoading
                        ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      }`}
                  >
                    {isLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-3" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-3" />
                        Verify OTP
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleResendCardOTP}
                    disabled={isLoading}
                    className={`inline-flex items-center justify-center w-full px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-300 ease-in-out
                      ${isLoading
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : "bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400"
                      }`}
                  >
                    {isLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-3" />
                        Resending...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faRedo} className="mr-3" />
                        Resend OTP
                      </>
                    )}
                  </button>
                  <button
                    onClick={closeModal}
                    className="inline-flex items-center justify-center w-full px-6 py-3 rounded-lg text-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors duration-200"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} className="mr-3" />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default WithdrawalsPage;